<template>
  <Teleport to="body">
    <SlotModal :display="visible" @close="keepEditing" class="text-fg-primary">
      <template #header>Unsaved Changes</template>

      <div class="relative">
        <XCloseIcon class="x-button absolute -top-7 right-0 cursor-pointer" @click="keepEditing" />
        <p id="modal-description">
          There are unsaved changes for this Performance. Do you want to save changes or discard
          before leaving?
        </p>
        <div class="flex items-center justify-between w-full mt-6 gap-6">
          <SlotButton id="discard-button" classes="w-1/3" @click="discardChanges" altWarning>
            Discard
          </SlotButton>
          <SlotButton id="save-button" classes="w-1/3" @click="saveChanges"> Save </SlotButton>
        </div>
      </div>
    </SlotModal>
  </Teleport>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router'

import XCloseIcon from '@/assets/xClose.svg?component'
import SlotButton from '@/common/components/atoms/SlotButton.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import {
  PERFORMANCE_UNSAVED_ACTIONS,
  PerformanceUnsavedActions,
} from '@/modules/performance/constants/PerformanceUnsavedActions'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'
import { isNavigatingAmongPerfFlow } from '@/router'

// eslint-disable-next-line no-unused-vars
type Resolve = (action: PerformanceUnsavedActions) => void

const visible = ref(false)
const resolve = ref<null | Resolve>(null)

function openModal(): Promise<PerformanceUnsavedActions> {
  visible.value = true
  return new Promise<PerformanceUnsavedActions>(res => (resolve.value = res))
}

const performanceUpdateStore = usePerformanceUpdateStore()
const { hasPerformanceUpdates } = storeToRefs(performanceUpdateStore)
function discardChanges() {
  visible.value = false
  performanceUpdateStore.discardUpdates()
  resolve.value?.(PERFORMANCE_UNSAVED_ACTIONS.DISCARD)
}
async function saveChanges() {
  visible.value = false
  await performanceUpdateStore.saveUpdates()
  resolve.value?.(PERFORMANCE_UNSAVED_ACTIONS.SAVE)
}
function keepEditing() {
  visible.value = false
  resolve.value?.(PERFORMANCE_UNSAVED_ACTIONS.STAY)
}

async function preventNavigationWhenUnsavedChanges(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  if (hasPerformanceUpdates.value && !isNavigatingAmongPerfFlow(to, from)) {
    const action = await openModal()
    next(action !== PERFORMANCE_UNSAVED_ACTIONS.STAY)
  } else next()
}
onBeforeRouteLeave(preventNavigationWhenUnsavedChanges)
onBeforeRouteUpdate(preventNavigationWhenUnsavedChanges)
</script>
